/* eslint-disable no-restricted-syntax */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { vaultApi } from '@modules/vault/api';
import {
  KYCCountry,
  UpdateKycData,
} from '@huspy/forge/types';
import { vaultFormUtils } from '@huspy/forge/features';
import { VaultFormValues } from '@modules/opportunities/presentation/pages/CaseCreation/views/FillBankForms/VaultForm/form-context';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';

const {
  cleanObject,
  transformResidentialInformation,
  transformEmploymentInformation,
  transformIncomeInformation,
  flattenValidationErrors,
} = vaultFormUtils;

/**
 * Custom hook for updating KYC data
 * @param opportunityId Unique identifier for the opportunity
 * @returns Mutation hook for updating KYC data
 */
export const useSubmitVault = <TCountry extends KYCCountry = 'AE'>(
  opportunityId: string,
  clientExternalId: string
) => {
  const queryClient = useQueryClient();

  const {
    mutate, mutateAsync, isPending, error,
  } = useMutation({
    mutationKey: ['submit-vault', opportunityId],
    mutationFn: async (data: VaultFormValues<TCountry>) => {
      trackAmplitudeEvent(
        USER_EVENTS.VAULT.CASE_CLIENT_REQUESTED_INFO_SUBMIT_ATTEMPTED,
        {
          case_id: opportunityId,
          client_id: clientExternalId,
        }
      );
      const applicantsData: UpdateKycData<KYCCountry>[] = data.applicants.map((applicant) => ({
        applicant_id: applicant.applicantId,
        applicant_type: applicant.applicantType,
        personal_information: cleanObject(applicant.personal_information),
        residential_information: transformResidentialInformation(
          applicant.residential_information
        ),
        employment_information: transformEmploymentInformation(
          applicant.employment_information
        ),
        financial_information: transformIncomeInformation({
          incomes: applicant.incomes,
          autoLoan: applicant.autoLoan,
          mortgage: applicant.mortgage,
          creditCard: applicant.creditCard,
          personalLoan: applicant.personalLoan,
        }),
      }));
      return vaultApi.submitVault(
        opportunityId,
        {
          applicants: applicantsData,
          actor_type: 'broker',
          set_vault_storage_submitted: true,
        }
      );
    },
    onSuccess: () => {
      trackAmplitudeEvent(
        USER_EVENTS.VAULT.CASE_CLIENT_REQUESTED_INFO_SUBMIT_COMPLETED,
        {
          case_id: opportunityId,
          client_id: clientExternalId,
        }
      );
      queryClient.setQueryData(['vault-storage', opportunityId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          vault_status: 'submitted',
        };
      });
      queryClient.invalidateQueries({ queryKey: ['vault-storage', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['clientOpportunity', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['kyc', opportunityId] });
    },
    onError: () => {
      trackAmplitudeEvent(
        USER_EVENTS.VAULT.CASE_CLIENT_REQUESTED_INFO_SUBMIT_FAILED,
        {
          case_id: opportunityId,
          client_id: clientExternalId,
        }
      );
    },
  });

  return {
    mutate,
    mutateAsync,
    error: flattenValidationErrors(error?.data || {}),
    isPending,
  };
};
